/*
 * [ATTENDANCE-2026-07-17-REBUILD] Staff attendance & payroll dashboard.
 * Superadmin/admin sees ALL staff totals + per-user drilldown modal.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, DollarSign, Users, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleLabelRu = {
  superadmin: 'Суперадмин',
  admin: 'Админ',
  sales_manager: 'Sales-менеджер',
  team_lead_sales: 'Тимлид Sales',
  retention_manager: 'Retention-менеджер',
  team_lead_retention: 'Тимлид Retention',
  manager: 'Менеджер',
};

const formatHours = (h) => {
  const num = Number(h) || 0;
  const totalMin = Math.round(num * 60);
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return `${hours}ч ${mins.toString().padStart(2, '0')}м`;
};

const money = (v) => `$${(Number(v) || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getTzOffsetMinutes = () => -new Date().getTimezoneOffset();

const SummaryTile = ({ icon: Icon, label, value, highlight, sub }) => (
  <div className={`rounded-lg p-4 border ${highlight ? 'border-indigo-300 bg-indigo-50 dark:bg-indigo-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1"><Icon className="w-4 h-4" /> {label}</div>
    <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
    {sub && <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">{sub}</div>}
  </div>
);

const AttendancePage = () => {
  const { tt } = useLanguage();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/staff-attendance/summary`, {
        params: { year, month, tz_offset: getTzOffsetMinutes() },
      });
      setSummary(data);
    } catch (e) {
      toast.error(tt('Не удалось загрузить сводку'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({ v: i + 1, l: new Date(2020, i, 1).toLocaleString('ru-RU', { month: 'long' }) }));
  const yearOptions = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  if (loading || !summary) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="attendance-loading">
        {tt('Загружаю…')}
      </div>
    );
  }

  return (
    <div data-testid="attendance-page">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-800"
          value={month}
          onChange={(e) => setMonth(+e.target.value)}
          data-testid="attendance-month-select"
        >
          {monthOptions.map((o) => (<option key={o.v} value={o.v}>{o.l}</option>))}
        </select>
        <select
          className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-800"
          value={year}
          onChange={(e) => setYear(+e.target.value)}
          data-testid="attendance-year-select"
        >
          {yearOptions.map((y) => (<option key={y} value={y}>{y}</option>))}
        </select>
        <div className="text-xs text-emerald-600 dark:text-emerald-400 ml-auto">
          ⓘ {tt('Время считается по вашему локальному часовому поясу')}
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <SummaryTile icon={Users} label={tt('Сотрудников')} value={summary.rows.length} />
        <SummaryTile
          icon={Clock}
          label={tt('Фактически отработано')}
          value={formatHours(summary.grand_hours_raw ?? summary.grand_hours)}
        />
        <SummaryTile
          icon={Clock}
          label={tt('Засчитано по графику')}
          value={formatHours(summary.grand_hours)}
          sub={(summary.grand_hours_raw != null && Math.abs((summary.grand_hours_raw || 0) - (summary.grand_hours || 0)) > 0.01) ? tt('часть времени урезана') : null}
        />
        <SummaryTile icon={DollarSign} label={tt('Фонд оплаты труда')} value={money(summary.grand_total_pay)} highlight />
      </div>

      {/* Table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-left">
              <tr>
                <th className="p-3">{tt('Сотрудник')}</th>
                <th className="p-3">{tt('Роль')}</th>
                <th className="p-3 text-right">{tt('Фактически')}</th>
                <th className="p-3 text-right">{tt('Засчитано')}</th>
                <th className="p-3 text-right">{tt('Дней')}</th>
                <th className="p-3 text-right">{tt('За часы')}</th>
                <th className="p-3 text-right">{tt('Бонусы')}</th>
                <th className="p-3 text-right">{tt('К выплате')}</th>
                <th className="p-3 text-right">{tt('Действия')}</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map((r) => {
                const raw = r.total_hours_raw ?? r.total_hours;
                const eff = r.total_hours;
                const trimmed = Math.abs((raw || 0) - (eff || 0)) > 0.01;
                return (
                  <tr key={r.user_id} className="border-t border-gray-100 dark:border-gray-700" data-testid={`attendance-row-${r.user_id}`}>
                    <td className="p-3 font-medium">{r.name}</td>
                    <td className="p-3 text-gray-500">{roleLabelRu[r.role] || r.role}</td>
                    <td className="p-3 text-right font-mono">{formatHours(raw)}</td>
                    <td className={`p-3 text-right font-mono ${trimmed ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}
                        title={trimmed ? tt('Часть времени урезана графиком') : ''}>
                      {formatHours(eff)}{trimmed && ' ⚠'}
                    </td>
                    <td className="p-3 text-right">{r.days_worked}</td>
                    <td className="p-3 text-right font-mono">{money(r.hours_pay)}</td>
                    <td className="p-3 text-right font-mono">{money(r.bonus_pay)}</td>
                    <td className="p-3 text-right font-mono font-semibold">{money(r.total_pay)}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => setSelected(r)} className="text-indigo-600 hover:underline" data-testid={`open-attendance-${r.user_id}`}>
                        {tt('Подробнее')}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!summary.rows.length && (
                <tr><td colSpan={9} className="p-6 text-center text-gray-500">{tt('Нет данных за выбранный месяц')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <DetailModal
          emp={selected}
          year={year}
          month={month}
          onClose={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
};


const DetailModal = ({ emp, year, month, onClose }) => {
  const { tt } = useLanguage();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [calc, sett] = await Promise.all([
        axios.get(`${API}/staff-salary/${emp.user_id}/calculate`, { params: { year, month, tz_offset: getTzOffsetMinutes() } }),
        axios.get(`${API}/staff-salary/${emp.user_id}`),
      ]);
      setData(calc.data);
      setSettings(sett.data);
    } catch (e) {
      toast.error(tt('Не удалось загрузить'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [emp.user_id, year, month]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/staff-salary/${emp.user_id}`, settings);
      toast.success(tt('Сохранено'));
      reload();
    } catch (e) {
      toast.error(tt('Не удалось сохранить'));
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold">{emp.name}</h2>
              <p className="text-sm text-gray-500">{roleLabelRu[emp.role] || emp.role}</p>
            </div>
            <button onClick={onClose} data-testid="close-modal-btn" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X className="w-5 h-5" /></button>
          </div>

          {loading || !data || !settings ? (
            <div className="text-center py-10 text-gray-500">{tt('Загружаю…')}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <SummaryTile icon={Clock} label={tt('Фактически')} value={formatHours(data.total_hours_raw ?? data.total_hours)} />
                <SummaryTile icon={Clock} label={tt('Засчитано')} value={formatHours(data.total_hours)} sub={(data.total_hours_raw != null && Math.abs((data.total_hours_raw || 0) - (data.total_hours || 0)) > 0.01) ? tt('часть урезана') : null} />
                <SummaryTile icon={DollarSign} label={tt('Оплата за часы')} value={money(data.hours_pay)} sub={`${money(data.hourly_rate)}/ч`} />
                <SummaryTile icon={DollarSign} label={tt('К выплате')} value={money(data.total_pay)} highlight />
              </div>

              {/* Settings */}
              <details className="mb-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                <summary className="cursor-pointer px-4 py-3 font-medium select-none bg-gray-50 dark:bg-gray-900">{tt('Настройки зарплаты и графика')}</summary>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label={tt('Оклад (мес)')} value={settings.base_salary} onChange={(v) => updateSetting('base_salary', +v || 0)} type="number" testid="base-salary" />
                  <Field label={tt('Норма часов в месяц')} value={settings.month_hours_norm} onChange={(v) => updateSetting('month_hours_norm', +v || 0)} type="number" testid="month-norm" />
                  <Field label={tt('Бонус за FTD ($)')} value={settings.bonus_per_ftd} onChange={(v) => updateSetting('bonus_per_ftd', +v || 0)} type="number" testid="bonus-ftd" />
                  <Field label={tt('Бонус за team FTD ($)')} value={settings.bonus_per_team_ftd} onChange={(v) => updateSetting('bonus_per_team_ftd', +v || 0)} type="number" testid="bonus-team-ftd" />
                  <Field label={tt('% с депозитов')} value={settings.deposit_percent} onChange={(v) => updateSetting('deposit_percent', +v || 0)} type="number" testid="dep-pct" />
                  <Field label={tt('% с team депозитов')} value={settings.team_deposit_percent} onChange={(v) => updateSetting('team_deposit_percent', +v || 0)} type="number" testid="team-dep-pct" />
                  <Field label={tt('Разовый бонус ($)')} value={settings.flat_bonus} onChange={(v) => updateSetting('flat_bonus', +v || 0)} type="number" testid="flat-bonus" />
                  <div />
                  <Field label={tt('Начало графика (HH:MM)')} value={settings.work_start_hhmm} onChange={(v) => updateSetting('work_start_hhmm', v)} testid="work-start" />
                  <Field label={tt('Конец графика (HH:MM)')} value={settings.work_end_hhmm} onChange={(v) => updateSetting('work_end_hhmm', v)} testid="work-end" />
                  <Check label={tt('Не считать до начала графика (clamp_start)')} checked={!!settings.clamp_start} onChange={(v) => updateSetting('clamp_start', v)} testid="clamp-start" />
                  <Check label={tt('Не считать после конца графика (clamp_end)')} checked={!!settings.clamp_end} onChange={(v) => updateSetting('clamp_end', v)} testid="clamp-end" />
                  <div className="md:col-span-2 flex justify-end">
                    <button onClick={saveSettings} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50" data-testid="save-settings-btn">
                      {saving ? tt('Сохраняю…') : tt('Сохранить настройки')}
                    </button>
                  </div>
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 md:col-span-2">ⓘ {tt('Время указывается и считается по вашему локальному часовому поясу.')}</p>
                </div>
              </details>

              {/* Daily table */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900 text-left">
                      <tr>
                        <th className="p-2">{tt('Дата')}</th>
                        <th className="p-2">{tt('Первый вход')}</th>
                        <th className="p-2">{tt('Последняя активность')}</th>
                        <th className="p-2 text-right">{tt('Фактически')}</th>
                        <th className="p-2 text-right">{tt('Засчитано')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.days.map((d) => {
                        const rowClass = d.status === 'absent'
                          ? 'bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800'
                          : d.status === 'late'
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800'
                          : d.status === 'weekend'
                          ? 'bg-gray-50 dark:bg-gray-900/40 border-t border-gray-200 dark:border-gray-700 text-gray-400'
                          : 'border-t border-gray-100 dark:border-gray-700';
                        const statusLabel = { absent: tt('отсутствовал'), late: tt('опоздал'), weekend: tt('выходной'), ok: '' }[d.status] || '';
                        const raw = d.hours_raw ?? d.hours;
                        const trimmed = Math.abs((raw || 0) - (d.hours || 0)) > 0.01;
                        return (
                          <tr key={d.date} className={rowClass} data-testid={`day-row-${d.date}`}>
                            <td className="p-2 font-medium">
                              {d.date}
                              {statusLabel && (<span className="ml-2 text-xs uppercase tracking-wide opacity-70">{statusLabel}</span>)}
                            </td>
                            <td className="p-2 text-gray-500">{d.first_login ? new Date(d.first_login).toLocaleTimeString('ru-RU') : '—'}</td>
                            <td className="p-2 text-gray-500">{d.last_activity ? new Date(d.last_activity).toLocaleTimeString('ru-RU') : '—'}</td>
                            <td className="p-2 text-right font-mono">{formatHours(raw)}</td>
                            <td className={`p-2 text-right font-mono ${trimmed ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}
                                title={trimmed ? tt('Часть времени урезана графиком') : ''}>
                              {formatHours(d.hours)}{trimmed && ' ⚠'}
                            </td>
                          </tr>
                        );
                      })}
                      {!data.days.length && (
                        <tr><td colSpan={5} className="p-4 text-center text-gray-500">{tt('Активности в этом месяце нет')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


const Field = ({ label, value, onChange, type = 'text', testid }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1">{label}</label>
    <input
      type={type}
      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-800"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    />
  </div>
);

const Check = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />
    <span className="text-sm">{label}</span>
  </label>
);

export default AttendancePage;
